import { ARB_WBTC_S_TOKEN_ADDRESS, getProtocolConfig } from "@silo/config";
import { siloIncentivesControllerAbi } from "@silo/contract-abi-types";
import {
  Address,
  Amount,
  ChainPublicClientService,
  makeAmount,
  ProtocolKeyService,
} from "@silo/types";
import { Effect } from "effect";
import { isNullable } from "effect/Predicate";
import { getContract } from "viem";
import { ContractsClientError } from "./types";

export type GetRewardsEmissions = ReturnType<typeof getRewardsEmissions>;
export type GetRewardsEmissionsSuccess =
  Effect.Effect.Success<GetRewardsEmissions>;
export type GetRewardsEmissionsError = Effect.Effect.Error<GetRewardsEmissions>;
export type GetRewardsEmissionsContext =
  Effect.Effect.Context<GetRewardsEmissions>;

interface GetRewardsEmissionsParams {
  collateralShareToken: Address;
  protectedShareToken: Address;
  debtShareToken: Address;
}

interface RewardsEmissions {
  collateralEmissionPerSecond: Amount;
  protectedEmissionPerSecond: Amount;
  debtEmissionPerSecond: Amount;
}

export function getRewardsEmissions({
  collateralShareToken,
  protectedShareToken,
  debtShareToken,
}: GetRewardsEmissionsParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;
    const { nativeRewards } = getProtocolConfig(protocolKey);

    const siloIncentivesControllerContract = nativeRewards
      ? getContract({
          address: nativeRewards.siloIncentivesControllerAddress,
          abi: siloIncentivesControllerAbi,
          client: publicClient,
        })
      : null;

    async function getEmissionPerSecond(shareToken: Address): Promise<bigint> {
      if (shareToken === ARB_WBTC_S_TOKEN_ADDRESS) {
        return 0n;
      }

      if (isNullable(siloIncentivesControllerContract)) {
        return 0n;
      }

      const [, emissionPerSecond] =
        await siloIncentivesControllerContract.read.getAssetData([shareToken]);

      return emissionPerSecond;
    }

    const rewardsEmissions = yield* Effect.tryPromise({
      try: async () => {
        const [
          collateralEmissionPerSecond,
          protectedEmissionPerSecond,
          debtEmissionPerSecond,
        ] = await Promise.all([
          getEmissionPerSecond(collateralShareToken).then(makeAmount),
          getEmissionPerSecond(protectedShareToken).then(makeAmount),
          getEmissionPerSecond(debtShareToken).then(makeAmount),
        ]);

        const rewardsEmissions: RewardsEmissions = {
          collateralEmissionPerSecond,
          protectedEmissionPerSecond,
          debtEmissionPerSecond,
        };

        return rewardsEmissions;
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    return rewardsEmissions;
  });
}
